import * as z from "zod";
import { pagination } from "../index";
import { type RetryWaitingInput, retryWaitingDto, retryWaitingInput, waitingItemCodes, waitingSources } from "../til";
import { receiptWaitingReasons } from "./enums";

// What the warehouse received against an inbound (50-api/procurement.md §2.7; I-11 `kind: receipt`, FR-M11-12,
// ADR-138): one receipt for each receipt of the warehouse, added to the lines of the inbound and of its PO, and the
// receipts Nexxt could not apply, which wait whole until the Logistics sets right what is missing.

/** The receipts of an inbound, the first one first. */
export const listInboundReceiptsInput = z.object({ inboundId: z.uuid() });
export type ListInboundReceiptsInput = z.input<typeof listInboundReceiptsInput>;

/** A line of a receipt: the units of an SKU on a line of the inbound, or of an SKU the ASN did not declare. */
export const receiptLineDto = z.object({
  id: z.uuid(),
  /** The PO line the units are on; null for an item the ASN did not declare. */
  poLineId: z.uuid().nullable(),
  lineNo: z.number().int().nullable(),
  skuId: z.uuid(),
  skuCode: z.string().nullable(),
  ean: z.string().nullable(),
  qtyReceived: z.number().int(),
});

/** A receipt of the warehouse, as the IMS reconciled it or GXO returned it (ADR-144), with its lines. */
export const receiptDto = z
  .object({
    id: z.uuid(),
    /** Its number in the system of the warehouse. */
    receiptRef: z.string(),
    locationCode: z.string(),
    locationName: z.string(),
    receivedAt: z.iso.datetime(),
    /** The units of all its lines. */
    units: z.number().int(),
    lines: z.array(receiptLineDto),
  })
  .meta({ id: "Receipt" });

// --- Receipts of the warehouse Nexxt could not apply (I-11 §5) ---

const waitingFilters = { reason: z.enum(receiptWaitingReasons).optional() };

/** The receipts of the warehouse that wait, the latest first: each lists at most 50 of its items (`itemCount`). */
export const listWaitingReceiptsInput = z.object({
  ...waitingFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListWaitingReceiptsInput = z.input<typeof listWaitingReceiptsInput>;

/** How many receipts wait with the same filters, and since when the oldest (ADR-086): the queue of the home. */
export const countWaitingReceiptsInput = z.object(waitingFilters);
export type CountWaitingReceiptsInput = z.input<typeof countWaitingReceiptsInput>;

/**
 * Applies again the receipts that wait, once the mapping, the SKU or the inbound is set right: each one applied goes,
 * the others wait with the reason of now.
 */
export const retryWaitingReceiptsInput = retryWaitingInput;
export type RetryWaitingReceiptsInput = RetryWaitingInput;
export const retryWaitingReceiptsDto = retryWaitingDto("WaitingReceiptsRetried");

/** An item the reason of a receipt that waits is about, with its codes as the warehouse sent them. */
export const waitingReceiptItem = z.object({ poLineNo: z.number().int().nullable(), ...waitingItemCodes });
export type WaitingReceiptItem = z.output<typeof waitingReceiptItem>;

/** A receipt of the warehouse that waits, whole: nothing of it is applied until all of it can be. */
export const waitingReceiptDto = z
  .object({
    id: z.uuid(),
    receiptRef: z.string(),
    /** The number of the ASN, as the warehouse sent it. */
    inboundNumber: z.string(),
    /** The system the fact came from (ADR-144): the warehouse and the items are in its codes. */
    source: z.enum(waitingSources),
    /** The warehouse in the codes of `source`. */
    locationCode: z.string(),
    receivedAt: z.iso.datetime(),
    /** The warehouse expects nothing more on the ASN. */
    inboundClosed: z.boolean(),
    reason: z.enum(receiptWaitingReasons),
    /** The first of the items the reason is about: none for the receipt as a whole. */
    items: z.array(waitingReceiptItem),
    /** How many items the reason is about. */
    itemCount: z.number().int(),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "WaitingReceipt" });
